import { useState, useEffect } from 'react';
import { WifiOff, CloudOff, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { 
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { isCurrentlyOnline, subscribeToOnlineStatus } from '@/hooks/useOfflineQueue';

interface OfflineIndicatorBadgeProps {
  pendingCount?: number;
  className?: string;
  showInHeader?: boolean;
}

export const OfflineIndicatorBadge = ({ 
  pendingCount = 0, 
  className,
  showInHeader = true 
}: OfflineIndicatorBadgeProps) => {
  const [isOnline, setIsOnline] = useState(isCurrentlyOnline());
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToOnlineStatus((online) => {
      if (!isOnline && online) {
        // Just came back online, show reconnecting state briefly
        setIsReconnecting(true);
        setTimeout(() => setIsReconnecting(false), 2000);
      }
      setIsOnline(online);
    });

    return unsubscribe;
  }, [isOnline]);

  // Don't show if online and no pending messages
  if (isOnline && pendingCount === 0 && !isReconnecting) {
    return null;
  }

  if (isReconnecting) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={cn(
              "gap-1.5 bg-success/10 border-success/30 text-success animate-pulse",
              className
            )}
          >
            <RefreshCw className="h-3 w-3 animate-spin" />
            <span className="text-xs">Syncing...</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>Reconnected! Syncing pending messages...</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge 
          variant="outline" 
          className={cn(
            "gap-1.5 border-destructive/30 text-destructive",
            isOnline ? "bg-warning/10 border-warning/30 text-warning" : "bg-destructive/10",
            className
          )}
        >
          {isOnline ? (
            <CloudOff className="h-3 w-3" />
          ) : (
            <WifiOff className="h-3 w-3 animate-pulse" />
          )}
          <span className="text-xs">
            {!isOnline ? 'Offline' : ''}
            {pendingCount > 0 && (
              <span className="ml-1">
                {!isOnline ? ' · ' : ''}{pendingCount} queued
              </span>
            )}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-sm">
          {!isOnline && <p className="font-medium">You're offline</p>}
          {pendingCount > 0 && (
            <p className="text-muted-foreground">
              {pendingCount} message{pendingCount > 1 ? 's' : ''} will be sent when you're back online
            </p>
          )}
          {!isOnline && pendingCount === 0 && (
            <p className="text-muted-foreground">Some features may not work properly</p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
